import React from 'react';
import {Box, Typography} from '@mui/material';
import {ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, LabelList} from 'recharts';
import {buildupSeries, monthLabel} from './participationSeries';

/* The participation build-up chart every dashboard shares (QA, 9 Oct 2026:
   "bar + line + values", so a single month still shows).

     <ParticipationBuildupChart card={trendCard}/>                       // framed, titled from the card
     <ParticipationBuildupChart card={card} threshold={{value: 15, label: '50% threshold (15 pupils)'}} bare/>

   Bars = learners added in the month, line = running total, both labelled.
   `threshold` draws a dashed reference line; `yMax` lifts the axis (e.g. the
   registered total); `bare` leaves out the card frame for a page that has one. */
const NAVY = '#052F44';
const TEAL = '#1B6B55';
const BAR = '#9CC9B8';

const Legend = () => (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap'}}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75}}>
            <Box sx={{width: 14, height: 10, borderRadius: '2px', backgroundColor: BAR}}/>
            <Typography sx={{fontSize: '12px', color: '#6A7282'}}>New learners in the month</Typography>
        </Box>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.75}}>
            <Box sx={{width: 18, height: 0, borderTop: `3px solid ${TEAL}`}}/>
            <Typography sx={{fontSize: '12px', color: '#6A7282'}}>Cumulative</Typography>
        </Box>
    </Box>
);

// The bar's own figure, inside the bar when it is tall enough, else just above it.
const BarValue = ({x, y, width, height, value}) => {
    if (value === undefined || value === null) return null;
    const inside = height >= 18;
    return (
        <text x={x + width / 2} y={inside ? y + 13 : y - 5} textAnchor="middle" fontSize={11} fontWeight={700} fill={inside ? '#fff' : '#4B5563'}>
            {value}
        </text>
    );
};

const TooltipBox = ({active, payload, label}) => {
    if (!active || !payload || !payload.length) return null;
    const row = payload[0].payload;
    return (
        <Box sx={{backgroundColor: '#fff', border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '8px', p: 1.25, boxShadow: '0 4px 12px rgba(0,0,0,0.08)'}}>
            <Typography sx={{fontSize: '12px', fontWeight: 700, color: NAVY, mb: 0.5}}>{monthLabel(label)}</Typography>
            <Typography sx={{fontSize: '12px', color: '#374151'}}>New learners: <strong>{row.added}</strong></Typography>
            <Typography sx={{fontSize: '12px', color: '#374151'}}>Cumulative: <strong>{row.cumulative}</strong></Typography>
        </Box>
    );
};

export const ParticipationBuildupChart = ({card, title, height = 260, threshold, yMax, emptyText = 'No participation recorded yet.', bare = false, sx}) => {
    const data = buildupSeries(card?.points);
    const maxCum = data.reduce((m, p) => Math.max(m, p.cumulative), 0);
    const top = Math.max(Number(yMax) || 0, Number(card?.y_max) || 0, maxCum, Number(threshold?.value) || 0, 1);
    const heading = title ?? card?.title ?? 'Participation build-up';

    const body = (
        <>
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 2}}>
                {!bare && <Typography sx={{fontSize: '16px', fontWeight: 700, color: NAVY}}>{heading}</Typography>}
                <Legend/>
            </Box>
            {data.length === 0 ? (
                <Box sx={{height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6A7282', fontSize: '13px'}}>{emptyText}</Box>
            ) : (
                <Box sx={{height, width: '100%'}}>
                    <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={data} margin={{top: 18, right: 24, left: -10, bottom: 0}} barCategoryGap="30%">
                            <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false}/>
                            <XAxis dataKey="label" tickFormatter={monthLabel} tick={{fontSize: 11, fill: '#6B7280'}} stroke="#E5E7EB" padding={{left: 30, right: 30}}/>
                            <YAxis domain={[0, Math.ceil(top * 1.1)]} allowDecimals={false} tick={{fontSize: 11, fill: '#6B7280'}} stroke="#E5E7EB"/>
                            <Tooltip content={<TooltipBox/>} cursor={{fill: 'rgba(5, 47, 68, 0.04)'}}/>
                            {threshold?.value != null && (
                                <ReferenceLine y={Number(threshold.value)} stroke="#9CA3AF" strokeDasharray="4 4" label={{value: threshold.label || `${threshold.value}`, position: 'insideTopRight', fontSize: 11, fill: '#6B7280'}}/>
                            )}
                            <Bar dataKey="added" name="New learners" fill={BAR} maxBarSize={36} radius={[4, 4, 0, 0]} isAnimationActive={false}>
                                <LabelList dataKey="added" content={<BarValue/>}/>
                            </Bar>
                            <Line type="monotone" dataKey="cumulative" name="Cumulative" stroke={TEAL} strokeWidth={3} dot={{r: 4, stroke: TEAL, strokeWidth: 2, fill: '#fff'}} activeDot={{r: 6}} isAnimationActive={false}>
                                <LabelList dataKey="cumulative" position="top" offset={10} style={{fontSize: 11, fontWeight: 700, fill: NAVY}}/>
                            </Line>
                        </ComposedChart>
                    </ResponsiveContainer>
                </Box>
            )}
        </>
    );

    if (bare) return <Box sx={sx}>{body}</Box>;
    return (
        <Box sx={{border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #fff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3, height: '100%', display: 'flex', flexDirection: 'column', ...sx}}>
            {body}
        </Box>
    );
};

export default ParticipationBuildupChart;
